import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { isAxiosError } from "axios";
import { useLocation, useNavigate, useParams } from "react-router";

import routes from "../../../routes";
import ErrorFallback from "../../common/ErrorFallback";
import Section from "../../solution/details/tabs/common/Section/Section";
import useProspectDetails from "../hooks/useProspectDetails";
import useProspectPrestoneId from "../hooks/useProspectPrestoneId";
import {
  buildProspectDetailsSections,
  getProspectScores,
  getProspectTitle,
} from "./model";
import PrestoneReportLink from "./PrestoneReportLink";
import ProspectScores from "./ProspectScores";

function ProspectDetails({ prospectId }: { prospectId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const openedDirectly = location.key === "default";
  const goBack = () =>
    openedDirectly ? navigate(routes.prospects.base({})) : navigate(-1);
  const { prospect, error, loading } = useProspectDetails(prospectId);
  const prestoneId = useProspectPrestoneId(prospect?.submissionId);

  if (error) {
    return (
      <>
        {isAxiosError(error) && error.response?.status === 404 ? (
          <Box
            data-testid="prospect-not-found"
            sx={{ paddingY: 4, textAlign: "center" }}
          >
            <Typography variant="h4">Prospect not found</Typography>
            <Typography sx={{ paddingTop: 1 }}>
              There is no prospect with the submission ID {prospectId}.
            </Typography>
          </Box>
        ) : (
          <ErrorFallback error={error} />
        )}
        <Button variant="contained" onClick={goBack}>
          Go Back
        </Button>
      </>
    );
  }

  if (loading) {
    return (
      <Box
        data-testid="prospect-details-loading-indicator"
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!prospect) {
    return null;
  }

  return (
    <Box
      data-testid="prospect-details-page-container"
      sx={{
        paddingTop: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1200, px: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            paddingBottom: 1,
            px: 4,
          }}
        >
          <Typography
            id="prospect-name-header"
            align="left"
            variant="h4"
            sx={{ flex: "1 1 20rem" }}
          >
            {getProspectTitle(prospect)}
          </Typography>
          <PrestoneReportLink prestoneId={prestoneId} />
        </Box>
        <ProspectScores {...getProspectScores(prospect)} />
        {buildProspectDetailsSections(prospect).map((section) => (
          <Section
            key={section.title}
            title={section.title}
            columns={1}
            fields={section.fields}
            highlightMissingFields={false}
          />
        ))}
      </Box>
    </Box>
  );
}

function ProspectDetailsPage() {
  const { id: prospectId } = useParams<{ id: string }>();
  if (!prospectId) {
    throw new Error("Prospect ID is required");
  }

  return <ProspectDetails key={prospectId} prospectId={prospectId} />;
}

export default ProspectDetailsPage;
